import React, { useEffect, useRef, useState } from 'react';

import { DatasetDataApi } from "api/DatasetDataApi";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { OverlayPanel } from "primereact/overlaypanel";
import { Tooltip } from "primereact/tooltip";
import { IUsersDataInfoResponse } from "../../../api/model/user/UsersDataInfoResponse";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import './UsersTable.scss';
import { formatISOToBasicDateFormat } from "./utils";

function UsersTable() {

    const [allData, setAllData] = useState<Array<IUsersDataInfoResponse>>([]);
    const [varData, setVarData] = useState<Array<IUsersDataInfoResponse>>([]);

    // EDIT DATA
    const [managers, setManagers] = useState<Array<IUsersDataInfoResponse>>([])

    const [loading, setLoading] = useState<boolean>(true)

    const [date1, setDate1] = useState<Date | undefined>(undefined)
    const [date2, setDate2] = useState<Date | undefined>(undefined)

    const op = useRef<OverlayPanel>(null);


    useEffect(() => {

        DatasetDataApi.getUsers()
            .then(({data, error}) => {
                if (error) {
                    return
                }
                if (data) {
                    if (data.length === 0) {
                        return
                    }

                    data = data.map((obj: IUsersDataInfoResponse) => {
                        if (obj.licenseAssignation) {
                            if (obj.licenseAssignation.expirationDate) {
                                obj.extraDate = new Date(obj.licenseAssignation?.expirationDate)
                            }
                        }

                        return obj;
                    })
                    setLoading(false);
                    setAllData(data);
                    setVarData(data)
                }
            })
    }, [])

    const managerEditor = (options: ColumnEditorOptions) => {
        return (
            <Dropdown value={options.value}
                      options={managers}
                      optionLabel="displayName"
                      optionValue="id"
                      onChange={(e) => {
                          if (options.editorCallback) {
                              return options.editorCallback(e.value)
                          }
                      }}
                      placeholder="Select a Manager"
            />
        );
    }

    const managerBody = (rowData: IUsersDataInfoResponse) => {
        let forLabel = "";
        if (rowData.firstName) {
            if (rowData.firstName.length > 0) {
                forLabel += rowData.firstName[0]
            }
        }
        if (rowData.lastName) {
            if (rowData.lastName.length > 0) {
                forLabel += rowData.lastName[0]
            }
        }
        if (forLabel.length > 0) {
            return (
                <div className="avatar-class">
                    <Tooltip target=".avatar-wrapper"/>
                    <span className="avatar-wrapper" data-pr-tooltip={rowData.displayName}>
                        <Avatar label={forLabel}
                                shape="circle"
                                style={{backgroundColor: '#2196F3', color: '#ffffff'}}/>
                    </span>
                </div>
            )
        } 
            
        

    }

    const tableHeader = (name: string) => {
        return <div className="table-header">
            {name}
            <Button onClick={(e) => op.current?.toggle(e)}
                    icon="pi pi-filter"
                    className="p-button-rounded p-button-text p-button-plain"
                    aria-haspopup
                    aria-controls="overlay_panel"
                    aria-label="Filter"/>
        </div>
    }

    const expirationBody = (rowData: IUsersDataInfoResponse) => {
        if (rowData.licenseAssignation?.expirationDate) {
            return formatISOToBasicDateFormat(rowData.licenseAssignation?.expirationDate);
        }
    }

    const dateSortButtonPressed = (e: any) => {
        op.current?.toggle(e)
        let newArr: Array<IUsersDataInfoResponse> = [];

        if (date1 && date2) {
            varData.forEach((obj: IUsersDataInfoResponse) => {
                if (obj.extraDate) {
                    if (obj.extraDate >= date1 && obj.extraDate <= date2) {
                        newArr.push(obj);
                    }
                }
            })
        } else if (date1 && !date2) {
            varData.forEach((obj: IUsersDataInfoResponse) => {
                if (obj.extraDate) {
                    if (obj.extraDate >= date1) {
                        newArr.push(obj);
                    }
                }
            })
        } else if (!date1 && date2) {
            varData.forEach((obj: IUsersDataInfoResponse) => {
                if (obj.extraDate) {
                    if (obj.extraDate <= date2) {
                        newArr.push(obj);
                    }
                }
            })
        } else {
            newArr = varData;
        }
        setAllData(newArr);
    }

    const dateClearButtonPressed = (e: any) => {
        op.current?.toggle(e)
        if (date1) {
            setDate1(undefined);
        }
        if (date2) {
            setDate2(undefined);
        }
        setAllData(varData);
    }

    const rowEditInitiated = (options: any) => {
        if (managers.length === 0) {
            DatasetDataApi.getUsers("LICENSE_MANAGER")
                .then(({data, error}) => {
                    if (error) {
                        return
                    }
                    if (data) {
                        if (data.length === 0) {
                            return
                        }
                        setManagers(data);
                    }
                })
        }
    }

    const rowEditCompleted = () => {
        console.log("complete")
    }

    const rowEditCanceled = () => {
        console.log("cancel")
    }

    const rowEditSaved = () => {
        console.log("save")
    }

    return (<>
            <DataTable value={allData}
                       responsiveLayout="scroll"
                       size="small"
                       showGridlines

                       removableSort
                       sortMode="multiple"

                       paginator
                       paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                       currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                       rows={10}
                       rowsPerPageOptions={[10, 20, 50]}

                       loading={loading}

                       editMode="row"
                       onRowEditComplete={rowEditCompleted}
                       onRowEditInit={rowEditInitiated}
                       onRowEditCancel={rowEditCanceled}
                       onRowEditSave={rowEditSaved}
            >
                <Column className="id not-editable" field="id" header="Id"
                        filter filterPlaceholder="Search by id"/>
                <Column className="username not-editable" field="username" header="Username"
                        sortable filter filterPlaceholder="Search by username"/>
                <Column className="name not-editable" field="firstName" header="Name"
                        sortable filter filterPlaceholder="Search by name"/>
                <Column className="surname not-editable" field="lastName" header="Surname"
                        sortable filter filterPlaceholder="Search by surname"/>
                <Column className="company-name not-editable" field="companyName" header="Company"
                        sortable filter filterPlaceholder="Search by company name"/>

                <Column className="license-id not-editable" field="licenseAssignation.licenseId" header="License"
                        filter filterPlaceholder="Search by license id"/>

                <Column className="expiration-date not-editable" field="extraDate"
                        sortable
                        body={expirationBody}
                        header={tableHeader("Expiration date")}/>

                <Column className="psm-max-version not-editable" field="licenseAssignation.psmMaxVersion"
                        header="Max. version"
                        filter filterPlaceholder="Search by EA-PSM max. version"
                />

                <Column className="manager" field="manager" header="Manager"
                        editor={(options) => managerEditor(options)}
                        filter filterPlaceholder="Search by manager"
                        body={managerBody}
                />

                {/* <Column field="deleted" header="Deleted"></Column> */}

                <Column className="editor"
                        rowEditor
                        bodyStyle={{textAlign: 'center'}}
                        onCellEditInit={(options) => {
                            rowEditInitiated(options)
                        }}
                />
            </DataTable>
            <OverlayPanel ref={op}
                          id="overlay_panel"
                          className="overlay-panel-expiration-date">
                <div className="calendar-flex">
                    <span style={{paddingLeft: "10px"}}>From:</span>
                    <CalendarWrapper value={date1}
                                     onChange={(e) => setDate1(e)}
                                     placeholder="Date from"
                                     showButtonBar
                    />
                </div>
                <div className="calendar-flex">
                    <span style={{paddingLeft: "10px"}}>To:</span>
                    <CalendarWrapper value={date2}
                                     onChange={(e) => setDate2(e)}
                                     placeholder="Date to"
                                     showButtonBar
                    />
                </div>

                <div className="button-flex">
                    <Button onClick={dateClearButtonPressed} className="p-button-sm p-button-outlined" label="Clear"/>
                    <Button onClick={dateSortButtonPressed} className="p-button-sm" label="Apply"/>
                </div>
            </OverlayPanel>
        </>
    );
}

export default UsersTable;